import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPhone, FaFacebook, FaInstagram, FaYelp, FaGoogle, FaChevronLeft, FaChevronRight, FaStar } from 'react-icons/fa';
import { scrollToSection } from '../../utils/scrollToSection';
import YelpModal from '../ui/YelpModal';
import './Navbar.css';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [autoHideTimeout, setAutoHideTimeout] = useState(null);
  const [isMiniNavOpen, setIsMiniNavOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isHoveringTop, setIsHoveringTop] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isYelpModalOpen, setIsYelpModalOpen] = useState(false);
  const miniNavRef = useRef(null);

  // Auto-open mini-nav for desktop and tablet only
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      // Desktop: > 1024px, Tablet: 769px - 1024px, Mobile: <= 768px
      return width > 768;
    };
    
    const deviceCheck = checkDevice();
    setIsDesktop(deviceCheck);
    
    if (deviceCheck) {
      setIsMiniNavOpen(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isHomepage = location.pathname === '/';
      
      // Clear any existing timeouts
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (autoHideTimeout) {
        clearTimeout(autoHideTimeout);
      }
      
      // Update scrolling state for mini nav toggle
      const scrolling = currentScrollY > 50;
      setIsScrolling(scrolling);
      
      // Close mini nav when user starts scrolling
      if (currentScrollY > 0 && isMiniNavOpen) {
        setIsMiniNavOpen(false);
      }
      
      // Always show navbar when at the very top of the page
      if (currentScrollY === 0) {
        setIsVisible(true);
        // Clear any pending auto-hide timeouts when at the top
        if (autoHideTimeout) {
          clearTimeout(autoHideTimeout);
          setAutoHideTimeout(null);
        }
      }
      // Show navbar when scrolling up or near the top
      else if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
        
        // Only set auto-hide timeout if not at the very top of the page
        if (currentScrollY > 0) {
          const timeout = setTimeout(() => {
            // Double-check we're not at the top before hiding
            if (window.scrollY > 0) {
              setIsVisible(false);
            }
          }, 2000);
          setAutoHideTimeout(timeout);
        }
      } 
      // Hide navbar when scrolling down (but not at the very top)
      else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // Minimal delay to prevent glitches
        const timeout = setTimeout(() => {
          setIsVisible(false);
        }, 50);
        setScrollTimeout(timeout);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (autoHideTimeout) {
        clearTimeout(autoHideTimeout);
      }
    };
  }, [lastScrollY, scrollTimeout, autoHideTimeout]);

  // Effect to add/remove blur class on body when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  // Handle hover effect at top of screen
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Check if mouse is within 50px of the top of the screen
      const isAtTop = e.clientY <= 50;
      
      if (isAtTop && !isVisible) {
        // Clear any existing timeout
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
        }
        setIsHoveringTop(true);
      } else if (!isAtTop && isHoveringTop) {
        // Set a timeout to hide navbar when mouse leaves the top area
        const timeout = setTimeout(() => {
          setIsHoveringTop(false);
        }, 300); // 300ms delay
        setHoverTimeout(timeout);
      }
    };

    const handleMouseLeave = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      setIsHoveringTop(false);
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [isVisible, isHoveringTop, hoverTimeout]);

  // Handle clicking outside mini-nav or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMiniNavOpen && !event.target.closest('.mini-nav')) {
        setIsMiniNavOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMiniNavOpen) {
        setIsMiniNavOpen(false);
      }
    };

    if (isMiniNavOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMiniNavOpen]);

  // Handle scrolling to section when navigating from other pages
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.substring(1); // Remove the # symbol
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    }
  }, [location.pathname, location.hash]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (autoHideTimeout) {
        clearTimeout(autoHideTimeout);
      }
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [scrollTimeout, autoHideTimeout, hoverTimeout]);

  // Handle focus outside mini-nav to close it
  useEffect(() => {
    const handleFocusOut = (event) => {
      if (isMiniNavOpen && miniNavRef.current) {
        // Check if the focused element is outside the mini-nav
        if (!miniNavRef.current.contains(event.target)) {
          setIsMiniNavOpen(false);
        }
      }
    };

    const handleClickOutside = (event) => {
      if (isMiniNavOpen && miniNavRef.current) {
        // Check if the clicked element is outside the mini-nav
        // Don't close if clicking on the toggle button (let it handle its own click)
        if (!miniNavRef.current.contains(event.target)) {
          setIsMiniNavOpen(false);
        }
      }
    };

    const handleKeyDown = (event) => {
      if (isMiniNavOpen && event.key === 'Escape') {
        setIsMiniNavOpen(false);
        // Return focus to the toggle button
        if (miniNavRef.current) {
          const toggleButton = miniNavRef.current.querySelector('.mini-nav-toggle');
          if (toggleButton) {
            toggleButton.focus();
          }
        }
      }
    };

    // Add event listeners
    document.addEventListener('focusin', handleFocusOut);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('focusin', handleFocusOut);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMiniNavOpen]);

  const handleScrollToSection = (sectionId) => {
    // If we're on the homepage, scroll to section
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      // If we're on another page, navigate to homepage with hash
      navigate(`/#${sectionId}`);
    }
    setIsMobileMenuOpen(false);
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    const choice = window.confirm("Call 313-631-6020?");
    
    if (choice) {
      window.location.href = 'tel:+13136316020';
    } else {
      // Cancel: do nothing (no auto-scroll)
      return;
    }
  };

  const handleYelpClick = (e) => {
    e.preventDefault();
    setIsYelpModalOpen(true);
  };

  // Check if we're on the homepage
  const isHomepage = location.pathname === '/';

  return (
    <>
      {/* Skip to main content link for keyboard accessibility */}
      <a href="#main-content" className="skip-to-main-content">
        Skip to main content
      </a>
      {/* Main navbar - Only show on homepage */}
      {isHomepage && (
        <>
          {/* Invisible hover zone at top of screen */}
          <div 
            className={`top-hover-zone ${!isVisible ? 'active' : ''}`}
            onMouseEnter={() => {
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
              }
              setIsHoveringTop(true);
            }}
            onMouseLeave={() => {
              const timeout = setTimeout(() => {
                setIsHoveringTop(false);
              }, 300);
              setHoverTimeout(timeout);
            }}
          />
          
          <nav className={`navbar ${isVisible || isMobileMenuOpen || isHoveringTop ? 'visible' : 'hidden'}`}>
            <div className="container">
              <div className="nav-content">
                <div className="nav-logo">
                  <div className="nav-logo-content">
                    <img 
                      alt="RC Roofing Logo" 
                      className="nav-logo-img" 
                      src="/images/logos/RC Roofing Yellow BG.jpg"
                    />
                    <div className="nav-logo-text">
                      <h1 className="nav-logo-title">RC ROOFING</h1>
                      <p className="nav-logo-subtitle">Where Vision Meets Precision</p>
                    </div>
                  </div>
                </div>
                <div className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`} role="list">
                  <a href="#home" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('home'); }}>Home</a>
                  <a href="#services" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('services'); }}>Services</a>
                  <a href="#portfolio" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('portfolio'); }}>Portfolio</a>
                  <a href="#reviews" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('reviews'); }}>Reviews</a>
                  <a href="#about" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('about'); }}>About</a>
                  <a href="#contact" role="listitem" onClick={(e) => { e.preventDefault(); handleScrollToSection('contact'); }}>Contact</a>
                </div>
                
                <div className="nav-cta">
                  <a href="#" className="contact-link" title="Contact: 313-631-6020" onClick={handleContactClick}>
                    <FaPhone aria-hidden="true" />
                  </a>
                  <button className="btn btn-primary btn-small" onClick={() => handleScrollToSection('contact')}>
                    Get Quote
                  </button>
                </div>
                
                <div className="mobile-controls">
                  <a href="#" className="contact-link mobile-contact-link" title="Contact: 313-631-6020" onClick={handleContactClick}>
                    <FaPhone aria-hidden="true" />
                  </a>
                  <button 
                    className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`} 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMobileMenuOpen}
                    type="button"
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </>
      )}
      
      {/* Mini Navigation Panel */}
      <div 
        ref={miniNavRef}
        className={`mini-nav ${isMiniNavOpen ? 'open' : 'closed'}`}
      >
        <button 
          className={`mini-nav-toggle ${isScrolling ? 'scrolling' : ''} ${isDesktop ? 'animated' : ''}`}
          onClick={() => setIsMiniNavOpen(!isMiniNavOpen)}
          aria-label={isMiniNavOpen ? 'Close mini navigation' : 'Open mini navigation'}
        >
          {isScrolling ? (
            isMiniNavOpen ? <FaChevronRight aria-hidden="true" /> : <FaChevronLeft aria-hidden="true" />
          ) : (
            <FaStar aria-hidden="true" />
          )}
        </button>
        
        <nav className="mini-nav-content" aria-label="Social media links">
          <div className="mini-nav-title">Follow Us</div>
          <div className="mini-nav-links">
            <a 
              href="https://www.facebook.com/profile.php?id=61574085062006" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="mini-nav-link"
              aria-label="Follow us on Facebook"
              tabIndex={isMiniNavOpen ? 0 : -1}
            >
              <FaFacebook aria-hidden="true" />
              <span>Facebook</span>
            </a>
            <a 
              href="https://www.instagram.com/rcroofings?igsh=MXU1bnQ5cjFmbm11YQ%3D%3D&utm_source=qr" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="mini-nav-link"
              aria-label="Follow us on Instagram"
              tabIndex={isMiniNavOpen ? 0 : -1}
            >
              <FaInstagram aria-hidden="true" />
              <span>Instagram</span>
            </a>
            <a 
              href="#" 
              onClick={handleYelpClick}
              className="mini-nav-link"
              aria-label="Check our Yelp reviews"
              tabIndex={isMiniNavOpen ? 0 : -1}
            >
              <FaYelp aria-hidden="true" />
              <span>Yelp</span>
            </a>
            <a 
              href="https://www.google.com/search?sca_esv=c9bfdf3b8cae865b&hl=en&authuser=0&sxsrf=AE3TifPDM3Ol_ow1rTGMUIRgwhrmU61Kvw:1759293247026&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-EzuQ3ppSlrXGcwVjVa_bVhBBa_K1m_PhUX93-hbnm5G7PtosDXz4RjQG9qYZLwRbUVK6TWBfyW1C-uoiVviAO13Q4fFa&q=RC+Remodel+Reviews&sa=X&ved=2ahUKEwjyyq2IloKQAxVoavUHHetZEzoQ0bkNegQIHxAC&biw=2560&bih=945&dpr=1" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="mini-nav-link"
              aria-label="Find us on Google Maps"
              tabIndex={isMiniNavOpen ? 0 : -1}
            >
              <FaGoogle aria-hidden="true" />
              <span>Google</span>
            </a>
          </div>
        </nav>
      </div>
      
      <YelpModal 
        isOpen={isYelpModalOpen}
        onClose={() => setIsYelpModalOpen(false)}
      />
    </>
  );
};

export default Navbar;
